import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Phone, 
  Check,
  ArrowRight,
  PhoneCall,
  Activity,
  X,
  Zap,
  Building2,
  Users,
  Headphones,
  Shield,
  MessageCircle,
  HelpCircle,
  Handshake
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

// Feature descriptions for tooltips
const featureDescriptions = {
  // Calling Features
  'WebRTC Softphone': 'Make and receive calls directly in your browser using WebRTC technology. No downloads or plugins required.',
  'Inbound & Outbound Calls': 'Full support for receiving incoming calls and making outgoing calls to any phone number.',
  'Call Transfer (Blind & Attended)': 'Transfer calls to colleagues. Blind transfer connects immediately; attended transfer lets you speak to the recipient first.',
  'Hold & Resume': 'Put calls on hold with music and resume them when ready. Callers hear hold music while waiting.',
  'Mute Controls': 'Quickly mute and unmute your microphone during calls without disconnecting.',
  'Call Recordings': 'Automatically record calls for training, compliance, or quality assurance purposes.',
  
  // Contacts & Presence
  'Contact Management': 'Create, edit, and organize your contacts. Add notes, tags, and custom fields.',
  'PBX Contact Sync': 'Automatically sync contacts from your Cloud PBX system. Changes sync in real-time.',
  'Basic Presence': 'See if team members are available, busy, or offline with simple status indicators.',
  'Real-time Presence Updates': 'Live presence updates showing exactly when someone starts or ends a call.',
  'Global Contacts Directory': 'Company-wide shared contact list accessible to all team members with appropriate permissions.',
  
  // History & Analytics
  'Call History': 'View detailed records of all calls including duration, time, and caller information.',
  'Queue Monitoring': 'Live dashboard showing active calls in queues, wait times, and agent availability.',
  'Analytics Dashboard': 'Visual reports on call volumes, answer rates, peak times, and team performance.',
  
  // AI Features
  'AI Transcripts': 'Automatic speech-to-text transcription of your calls with timestamps.',
  'Call Summaries': 'AI-generated summaries highlighting key points and action items from each call.',
  'Sentiment Analysis': 'AI analysis of call sentiment showing whether conversations were positive, negative, or neutral.',
  
  // Admin & Support
  'User Limit': 'Maximum number of users allowed on your plan.',
  'Role-based Permissions': 'Control what features each user can access based on their role (User, Admin, etc.).',
  'Support': 'How you can get help - Community forums, email support, or priority email with faster response times.'
};

// Helper component for feature with tooltip
const FeatureWithTooltip = ({ feature, pbxDependent = false }) => (
  <div className="flex items-center gap-2">
    <span className="text-slate-300">
      {feature}
      {pbxDependent && <span className="text-amber-400 ml-1">*</span>}
    </span>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 text-slate-500 hover:text-cyan-400 cursor-help transition-colors" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-800 text-slate-200 border border-slate-700">
        <p className="text-sm">{featureDescriptions[feature] || 'Feature description coming soon.'}</p>
      </TooltipContent>
    </Tooltip>
  </div>
);

export default function Pricing() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set page title
  useEffect(() => {
    document.title = "Connect | Pricing";
    window.scrollTo(0, 0);
  }, []);

  // Fetch pricing from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/pricing`);
        setPricing(response.data.plans);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Fall back to defaults if API fails
        setPricing({
          free: { price_monthly: 0 },
          pro: { price_monthly: 20 }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  // Get price from API response or use fallback
  const getPrice = (plan) => {
    if (!pricing) return plan === 'free' ? 0 : 20;
    // Check for both new format (retail_price_monthly) and legacy (price_monthly)
    return pricing[plan]?.retail_price_monthly || pricing[plan]?.price_monthly || 0;
  };

  const plans = [
    {
      name: 'Free',
      key: 'free',
      description: 'Perfect for trying out CONNECT',
      period: 'forever',
      users: 'Up to 2 users',
      popular: false,
      features: [
        { text: 'WebRTC Softphone', included: true },
        { text: 'Contact Management', included: true },
        { text: 'Call History (30 days)', included: true, pbxDependent: true },
        { text: 'Basic Presence', included: true },
        { text: 'Community Support', included: true },
        { text: 'Queue Monitoring', included: false, pbxDependent: true },
        { text: 'Analytics Dashboard', included: false, pbxDependent: true },
        { text: 'AI Features', included: false, pbxDependent: true }
      ],
      cta: 'Get Started Free',
      ctaVariant: 'outline'
    },
    {
      name: 'Pro',
      key: 'pro',
      description: 'Everything you need for your team',
      period: 'user/month',
      users: 'Unlimited users',
      popular: true,
      features: [
        { text: 'WebRTC Softphone', included: true },
        { text: 'Contact Management', included: true },
        { text: 'Call History (Unlimited)', included: true, pbxDependent: true },
        { text: 'Real-time Presence', included: true, pbxDependent: true },
        { text: 'Queue Monitoring', included: true, pbxDependent: true },
        { text: 'Analytics Dashboard', included: true, pbxDependent: true },
        { text: 'Call Recordings', included: true, pbxDependent: true },
        { text: 'AI Transcripts & Summaries', included: true, pbxDependent: true },
        { text: 'Sentiment Analysis', included: true, pbxDependent: true },
        { text: 'Global Contacts Directory', included: true },
        { text: 'Advanced Role Permissions', included: true },
        { text: 'Priority Email Support', included: true }
      ],
      cta: 'Start Free Trial',
      ctaVariant: 'primary'
    }
  ];

  const faqs = [
    {
      q: 'Is there a free trial?',
      a: 'Yes! The Pro plan comes with a 14-day free trial. No credit card required to start.'
    },
    {
      q: 'Which PBX systems do you integrate with?',
      a: 'CONNECT integrates with leading Cloud PBX providers. This includes full SIP/WebRTC connectivity, call history, AI transcription, and real-time presence. More integrations coming soon!'
    },
    {
      q: 'Can I change plans later?',
      a: 'Absolutely. You can upgrade from Free to Pro at any time. Changes take effect immediately.'
    },
    {
      q: 'Do you offer annual billing?',
      a: 'Yes, we offer a 20% discount for annual billing. Contact us for details.'
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept all major credit cards (Visa, Mastercard, Amex) and can arrange invoicing for larger teams.'
    },
    {
      q: 'Is there a minimum number of users?',
      a: 'The Free plan supports up to 2 users. The Pro plan has no user limits - pay only for what you need.'
    },
    {
      q: 'Do I need special hardware?',
      a: 'No! CONNECT works entirely in your browser. All you need is a computer with a microphone and internet connection.'
    }
  ];

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-slate-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-cyan-400 font-medium">Pricing</Link>
            <Link to="/business" className="text-slate-300 hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="text-slate-300 hover:text-white transition-colors">Partners</Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                Sign In
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25">
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-8">
            <Building2 className="w-4 h-4" />
            Simple, Predictable Pricing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Pricing that works for
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              you and your clients
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Transparent per-user pricing with no hidden fees. Free tier available for your customers to trial. Volume discounts for resellers.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {plans.map((plan, i) => (
              <div 
                key={i}
                className={`relative rounded-2xl p-8 ${
                  plan.popular 
                    ? 'bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border-2 border-cyan-500/50' 
                    : 'bg-slate-800/30 border border-slate-700/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-slate-400 text-sm">{plan.description}</p>
                </div>

                <div className="text-center mb-2">
                  {plan.key === 'free' ? (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-white">Free</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-white">${getPrice(plan.key)}</span>
                      <span className="text-slate-400">/{plan.period}</span>
                    </div>
                  )}
                </div>
                
                <div className="text-center mb-8">
                  <span className="text-sm text-cyan-400">{plan.users}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-3">
                      {feature.included ? (
                        <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-cyan-400" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-slate-700/50 flex items-center justify-center shrink-0">
                          <X className="w-3 h-3 text-slate-500" />
                        </div>
                      )}
                      <span className={feature.included ? 'text-slate-300' : 'text-slate-500'}>
                        {feature.text}
                        {feature.pbxDependent && <span className="text-amber-400 ml-1">*</span>}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to="/login?tab=register" className="block">
                  <Button 
                    className={`w-full py-6 ${
                      plan.ctaVariant === 'primary'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                        : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          )}
          
          {/* PBX-dependent footnote */}
          <p className="text-center text-sm text-slate-500 mt-6">
            <span className="text-amber-400">*</span> Feature availability depends on your Cloud PBX provider&apos;s API support
          </p>
        </div>
      </section>

      {/* Detailed Feature Matrix */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Compare Plans
            </h2>
            <p className="text-slate-400">Detailed feature breakdown by plan</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-white font-medium">Free</th>
                  <th className="text-center py-4 px-4 text-cyan-400 font-medium">Pro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {/* Calling Features */}
                <tr className="bg-slate-800/30">
                  <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-cyan-400">Calling Features</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="WebRTC Softphone" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Inbound & Outbound Calls" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Call Transfer (Blind & Attended)" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Hold & Resume" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Mute Controls" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Call Recordings" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>

                {/* Contacts & Presence */}
                <tr className="bg-slate-800/30">
                  <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-cyan-400">Contacts & Presence</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Contact Management" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="PBX Contact Sync" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Basic Presence" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Real-time Presence Updates" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Global Contacts Directory" /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>

                {/* History & Analytics */}
                <tr className="bg-slate-800/30">
                  <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-cyan-400">History & Analytics</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Call History" pbxDependent /></td>
                  <td className="py-3 px-4 text-center text-slate-400 text-sm">30 days</td>
                  <td className="py-3 px-4 text-center text-green-400 text-sm">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Queue Monitoring" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Analytics Dashboard" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>

                {/* AI Features */}
                <tr className="bg-slate-800/30">
                  <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-cyan-400">AI Features</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="AI Transcripts" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Call Summaries" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Sentiment Analysis" pbxDependent /></td>
                  <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-600 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-green-400 mx-auto" /></td>
                </tr>

                {/* Admin & Support */}
                <tr className="bg-slate-800/30">
                  <td colSpan={3} className="py-3 px-4 text-sm font-semibold text-cyan-400">Admin & Support</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="User Limit" /></td>
                  <td className="py-3 px-4 text-center text-slate-400 text-sm">2 users</td>
                  <td className="py-3 px-4 text-center text-green-400 text-sm">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Role-based Permissions" /></td>
                  <td className="py-3 px-4 text-center text-slate-400 text-sm">Basic</td>
                  <td className="py-3 px-4 text-center text-green-400 text-sm">Advanced</td>
                </tr>
                <tr>
                  <td className="py-3 px-4"><FeatureWithTooltip feature="Support" /></td>
                  <td className="py-3 px-4 text-center text-slate-400 text-sm">Community</td>
                  <td className="py-3 px-4 text-center text-green-400 text-sm">Priority Email</td>
                </tr>

                {/* Pricing */}
                <tr className="bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border-t-2 border-cyan-500/30">
                  <td className="py-5 px-4 text-white font-semibold">Price</td>
                  <td className="py-5 px-4 text-center">
                    <div className="text-2xl font-bold text-white">Free</div>
                    <div className="text-xs text-slate-400">Up to 2 users</div>
                  </td>
                  <td className="py-5 px-4 text-center">
                    <div className="text-2xl font-bold text-cyan-400">${getPrice('pro')}</div>
                    <div className="text-xs text-slate-400">per user/month</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* PBX-dependent footnote */}
          <p className="text-sm text-slate-500 mt-4">
            <span className="text-amber-400">*</span> Feature availability depends on your Cloud PBX provider&apos;s API support
          </p>
        </div>
      </section>

      {/* All Plans Include */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              All plans include
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { icon: Phone, title: 'Unlimited Calls', desc: 'No per-minute charges' },
              { icon: Shield, title: 'Encrypted Calls', desc: 'Enterprise-grade security' },
              { icon: Headphones, title: 'HD Voice', desc: 'Crystal-clear audio' },
              { icon: Users, title: 'Team Features', desc: 'Presence & transfers' },
              { icon: Building2, title: 'Multi-device', desc: 'Use on any device' },
              { icon: MessageCircle, title: 'Support', desc: 'Help when you need it' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{item.title}</h4>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Frequently asked questions
            </h2>
            <p className="text-slate-400">
              Have more questions? <Link to="/help" className="text-cyan-400 hover:underline">Contact our support team</Link>
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <h4 className="text-white font-medium mb-2">{faq.q}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/20 p-8 sm:p-12 text-center">
            <Zap className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              Join hundreds of teams already using CONNECT to manage their business calls more effectively.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/subscribe">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-6 text-lg shadow-xl shadow-cyan-500/25">
                  Start 14-Day Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <p className="text-slate-500 text-sm mt-4">No credit card required</p>
          </div>
        </div>
      </section>

      {/* Partner CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm mb-6">
            <Handshake className="w-4 h-4" />
            Reseller Program
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Resell CONNECT to Your Customers
          </h2>
          <p className="text-slate-400 text-lg mb-6 max-w-2xl mx-auto">
            Are you a PBX reseller, IT consultant, or MSP? Join our partner program and get 
            wholesale pricing to resell CONNECT to your customers.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm">
            <span className="px-4 py-2 rounded-full bg-slate-800/80 text-slate-300 border border-slate-700">
              <Check className="w-4 h-4 inline mr-2 text-green-400" />
              NFR subscription available
            </span>
            <span className="px-4 py-2 rounded-full bg-slate-800/80 text-slate-300 border border-slate-700">
              <Check className="w-4 h-4 inline mr-2 text-green-400" />
              Volume discounts available
            </span>
            <span className="px-4 py-2 rounded-full bg-slate-800/80 text-slate-300 border border-slate-700">
              <Check className="w-4 h-4 inline mr-2 text-green-400" />
              Free tier for customer trials
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/partner/login?tab=register">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white px-8">
                Become a Reseller
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/partner/login">
              <Button size="lg" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8">
                <Building2 className="w-5 h-5 mr-2" />
                Partner Portal
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold">CONNECT</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/features" className="hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="hover:text-white transition-colors">Partners</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">© 2025 Connect-US. All rights reserved.</p>
        </div>
      </footer>
    </div>
    </TooltipProvider>
  );
}
